// Copyright 2026 AllSpice. All rights reserved.

import {SCHEMA_TYPES, type SchemaType} from '../../features/allspicejson.ts';

// Filter paths and grouped filter item IDs share this delimiter so the filter
// tree, override persistence, and path normalization all speak the same format.
export const ATTRIBUTES_DELIMITER = '|x42~|';

// Top-level category fields returned by Crysknife for each schema type.
// Paths that already start with one of these are already normalized.
export const SCHEMATIC_FIELDS = ['components', 'junctions', 'no_connects', 'ports', 'sheet_refs', 'wires'] as const;
export const PCB_FIELDS = ['arcs', 'pads', 'regions', 'tracks', 'texts', 'vias'] as const;
const KNOWN_CATEGORY_FIELDS = new Set<string>([
  ...SCHEMATIC_FIELDS,
  'graphics',
  'component_instances',
  ...PCB_FIELDS,
  'padstacks',
]);

export function makeDiffFilterTargetId(pageId: string | null | undefined, dataId: string): string {
  if (!pageId) {
    return dataId;
  }

  return `${pageId}${ATTRIBUTES_DELIMITER}${dataId}`;
}

export function parseDiffFilterTargetId(targetId: string): {pageId: string | null; dataId: string} {
  const delimiterIndex = targetId.indexOf(ATTRIBUTES_DELIMITER);
  if (delimiterIndex === -1) {
    return {
      pageId: null,
      dataId: targetId,
    };
  }

  return {
    pageId: targetId.substring(0, delimiterIndex),
    dataId: targetId.substring(delimiterIndex + ATTRIBUTES_DELIMITER.length),
  };
}

// Categories whose entities buildFilterDataFromContents indexes by data ID.
// Diff-overlay elements outside these categories (net_labels, harnesses, ...)
// can't be looked up, so overlay suppression must leave them visible.
const OVERLAY_INDEXED_FIELDS = new Set<string>(SCHEMATIC_FIELDS);

/**
 * Builds the lookup key used to decide whether a diff-overlay SVG element has
 * meaningful (visual) changes. Returns null when the element can't be looked
 * up in the filter data — callers must keep such elements visible.
 *
 * SVG `data-path` values use `/` separators and may nest
 * (`sheet_refs/<id>/sheet_entries`), while the filter data is keyed by the
 * top-level entity's id and category (`sheet_refs${ATTRIBUTES_DELIMITER}<id>`),
 * with page graphics keyed by sub-type (`texts${ATTRIBUTES_DELIMITER}<id>`).
 */
export function overlayDiffVisibilityKey(dataPath: string, dataId: string): string | null {
  const segments = dataPath.split('/').filter(Boolean);
  const category = segments[0];
  if (!category) return null;

  if (category === 'graphics') {
    return segments[1] ? `${segments[1]}${ATTRIBUTES_DELIMITER}${dataId}` : null;
  }

  if (!OVERLAY_INDEXED_FIELDS.has(category)) {
    return null;
  }

  // Nested elements (e.g. sheet_refs/<id>/sheet_entries) are indexed under
  // their top-level parent's id, not their own data-id.
  if (segments.length > 1) {
    return `${category}${ATTRIBUTES_DELIMITER}${segments[1]}`;
  }

  return `${category}${ATTRIBUTES_DELIMITER}${dataId}`;
}

export function normalizeFilterPath(path: string): string {
  const delimiterIndex = path.indexOf(ATTRIBUTES_DELIMITER);
  const firstSegment = delimiterIndex === -1 ? path : path.substring(0, delimiterIndex);

  if (KNOWN_CATEGORY_FIELDS.has(firstSegment)) return path;
  if (delimiterIndex === -1) return '';

  return path.substring(delimiterIndex + ATTRIBUTES_DELIMITER.length);
}

export function determineSchemaType(
  headContents: Record<string, unknown> | null | undefined,
  baseContents: Record<string, unknown> | null | undefined,
): SchemaType {
  const schemaType = headContents?.['type'] ?? baseContents?.['type'];
  return typeof schemaType === 'string' ? schemaType as SchemaType : SCHEMA_TYPES.UNKNOWN;
}
